import { ErrorToast, reportError } from '@vgrubs/components';
import { useEffect, useRef, useState } from 'react';
import {
  getMenuImportAiPreview,
  uploadMenuImportAi,
} from '../../api/menu.api';
import { MenuV2Response } from '../../types/menu.types';
import { importMenuV2DtoToMenuV2Response } from './menuImportAi.helpers';
import useMenuImportAiSocket, {
  MenuImportAiFinishedPayload,
} from './MenuImportAiSocket';

// Safety net: if the socket event never arrives (e.g. dropped connection),
// stop the loader instead of hanging forever.
const TIMEOUT_MS = 180000; // 3 min

/**
 * Orchestrates the "Import Menu with AI" flow: upload a document (+ optional
 * prompt) to start a worker job, subscribe to the store's menu socket, and wait
 * for the `menu_import_ai_finished` event. On completion it fetches the parsed
 * preview once, converts the `ImportMenuV2Dto` to a `MenuV2Response`, and hands
 * it to `onReady` (which feeds the existing menu preview editor). Failures /
 * timeouts surface a toast and stop the loader.
 */
export const useMenuImportAi = ({
  locationId,
  brandId,
  allowedToEditMenuPrice,
  onReady,
}: {
  locationId: string | number;
  brandId: string | number;
  allowedToEditMenuPrice?: boolean;
  onReady: (menu: MenuV2Response) => void;
}) => {
  const [aiLoading, setAiLoading] = useState(false);

  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  const jobIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Points at the latest finished-handler so the socket always runs a fresh
  // closure (current ids / props) without reconnecting.
  const handleFinishedRef = useRef<
    (payload: MenuImportAiFinishedPayload) => void
  >(() => undefined);

  const { observeMenu } = useMenuImportAiSocket({
    onFinished: (payload) => handleFinishedRef.current(payload),
  });

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const finish = () => {
    clearTimer();
    jobIdRef.current = null;
    setAiLoading(false);
    observeMenu(locationId, brandId, false);
  };

  // The worker signaled completion -> fetch the parsed menu once.
  const resolvePreview = async (jobId: string) => {
    try {
      const { data } = await getMenuImportAiPreview(locationId, brandId, jobId);
      const { status, data: previewData, error } = data.data;

      if (status === 'ready' && previewData) {
        onReadyRef.current(
          importMenuV2DtoToMenuV2Response(previewData, allowedToEditMenuPrice)
        );
      } else {
        ErrorToast(error || 'Menu import failed. Please try again.');
      }
    } catch (error) {
      reportError(error);
    } finally {
      finish();
    }
  };

  handleFinishedRef.current = (payload: MenuImportAiFinishedPayload) => {
    // ignore events for other jobs / when nothing is in flight
    if (!jobIdRef.current || payload.jobId !== jobIdRef.current) return;
    clearTimer();
    resolvePreview(payload.jobId);
  };

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, []);

  const startAiImport = async (file: File, subPrompt: string) => {
    if (!file) return;

    clearTimer();
    setAiLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const trimmedPrompt = subPrompt?.trim();
      if (trimmedPrompt) formData.append('subPrompt', trimmedPrompt);

      const { data } = await uploadMenuImportAi(locationId, brandId, formData);
      const { jobId } = data.data;
      if (!jobId) throw new Error('Missing jobId from AI import upload');

      jobIdRef.current = jobId;
      // subscribe to this store's menu room so we receive the finished event
      observeMenu(locationId, brandId, true);

      timeoutRef.current = setTimeout(() => {
        ErrorToast('Menu import timed out. Please try again.');
        finish();
      }, TIMEOUT_MS);
    } catch (error) {
      setAiLoading(false);
      reportError(error);
    }
  };

  return { startAiImport, aiLoading, setAiLoading };
};
